import { defaultBlockSpecs } from "@blocknote/core";
import { filterSuggestionItems } from "@blocknote/core/extensions";
import { BlockNoteView } from "@blocknote/mantine";
import {
  createReactBlockSpec,
  type DefaultReactSuggestionItem,
  getDefaultReactSlashMenuItems,
  SideMenuController,
  SuggestionMenuController,
  type SuggestionMenuProps,
  useCreateBlockNote,
} from "@blocknote/react";
import {
  cloneElement,
  isValidElement,
  type ReactElement,
  useMemo,
} from "react";
import "@blocknote/core/fonts/inter.css";
import "@blocknote/mantine/style.css";
import { useYDoc, useYjsProvider } from "../../state/yjs-provider";
import { CompactSideMenu } from "./blocknote-side-menu";
import { useSyncBridge } from "./use-sync-bridge";

const CharacterSheetBlock = createReactBlockSpec(
  {
    type: "character-sheet",
    propSchema: {
      name: { default: "New Token", type: "string" },
      hp: { default: "10", type: "string" },
    },
    content: "none",
  },
  {
    render: (props) => (
      <div className="flex flex-col gap-1.5 p-2 my-1 border border-white/10 rounded bg-white/5">
        <div className="flex items-center justify-between">
          <input
            className="bg-transparent text-white/90 font-semibold outline-none text-[13px]"
            value={props.block.props.name}
            onChange={(e) =>
              props.editor.updateBlock(props.block, {
                props: { name: e.target.value },
              })
            }
            placeholder="Token Name"
          />
          <div className="flex items-center gap-1.5 text-white/60 text-[11px]">
            <span className="text-white/30 uppercase text-[9px] tracking-widest">
              HP
            </span>
            <input
              className="bg-white/5 w-10 text-center rounded outline-none border border-white/10 text-[11px] text-white/80"
              value={props.block.props.hp}
              onChange={(e) =>
                props.editor.updateBlock(props.block, {
                  props: { hp: e.target.value },
                })
              }
            />
          </div>
        </div>
        <span className="text-[9px] text-white/25 uppercase tracking-widest">
          Spatial Token Entity
        </span>
      </div>
    ),
  },
);

function CustomSlashMenu(
  props: SuggestionMenuProps<DefaultReactSuggestionItem>,
) {
  const { items, selectedIndex, onItemClick, loadingState } = props;

  let prevGroup: string | undefined;

  return (
    <div className="w-[320px] max-w-[80vw] max-h-[420px] overflow-y-auto rounded border border-white/10 bg-black/85 backdrop-blur-sm p-[2px] shadow-2xl">
      {items.map((item, index) => {
        const group = item.group?.toUpperCase();
        const showGroup = group && group !== prevGroup;
        prevGroup = group;

        return (
          <div key={`${item.title}-${index}`}>
            {showGroup && (
              <div className="px-2 pt-2 pb-1 text-[9px] font-bold tracking-[0.1em] uppercase text-white/30">
                {group}
              </div>
            )}

            <button
              type="button"
              role="option"
              aria-selected={selectedIndex === index}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => onItemClick?.(item)}
              className={`w-full h-7 px-1.5 rounded flex items-center gap-2 text-left transition-colors ${
                selectedIndex === index ? "bg-white/10" : "hover:bg-white/7"
              }`}
            >
              <span className="w-5 h-5 shrink-0 flex items-center justify-center text-white/90">
                {item.icon}
              </span>

              <span className="flex-1 min-w-0 truncate text-[11px] leading-none text-white/82">
                {item.title}
              </span>

              {item.badge && (
                <span className="shrink-0 px-1 py-0.5 rounded border border-white/10 bg-white/6 text-[8px] leading-none text-white/35">
                  {item.badge}
                </span>
              )}
            </button>
          </div>
        );
      })}

      {loadingState !== "loaded" && (
        <div className="px-2 py-2 text-[10px] text-white/35">Loading…</div>
      )}
    </div>
  );
}

export function BlockNoteEditor() {
  const doc = useYDoc();
  const provider = useYjsProvider();

  const editor = useCreateBlockNote({
    blockSpecs: {
      ...defaultBlockSpecs,
      "character-sheet": CharacterSheetBlock,
    },
    collaboration: {
      provider,
      fragment: doc.getXmlFragment("document"),
      user: {
        name: "Player",
        color: `#${Math.floor(Math.random() * 16777215).toString(16)}`,
      },
    },
  });

  // Sync H1 headings and character-sheet blocks into the tree view
  useSyncBridge(editor, doc, { headingLevel: 1 });

  const slashItems = useMemo(
    () =>
      getDefaultReactSlashMenuItems(editor).map((item) => {
        if (!isValidElement(item.icon)) return item;

        return {
          ...item,
          // Size icons using BlockNote's supported extension API rather than internal CSS classes.
          icon: cloneElement(item.icon as ReactElement<{ size?: number }>, {
            size: 16,
          }),
        };
      }),
    [editor],
  );

  const getSlashItems = useMemo(
    () => async (query: string) => filterSuggestionItems(slashItems, query),
    [slashItems],
  );

  if (!editor) return null;

  return (
    <div className="h-full w-full overflow-y-auto blocknote-theme">
      <BlockNoteView
        editor={editor}
        slashMenu={false}
        sideMenu={false}
        theme={{
          colors: {
            editor: {
              background: "transparent",
              text: "rgba(255,255,255,0.85)",
            },
            menu: {
              background: "rgba(0,0,0,0.85)",
              text: "rgba(255,255,255,0.8)",
            },
            tooltip: {
              background: "rgba(0,0,0,0.85)",
              text: "rgba(255,255,255,0.7)",
            },
            hovered: {
              background: "rgba(255,255,255,0.07)",
              text: "rgba(255,255,255,0.9)",
            },
            selected: {
              background: "rgba(255,255,255,0.12)",
              text: "rgba(255,255,255,0.95)",
            },
            disabled: {
              background: "transparent",
              text: "rgba(255,255,255,0.2)",
            },
            shadow: "rgba(0,0,0,0.5)",
            border: "rgba(255,255,255,0.1)",
            sideMenu: "rgba(255,255,255,0.25)",
            highlights: {
              gray: {
                background: "rgba(255,255,255,0.06)",
                text: "rgba(255,255,255,0.6)",
              },
              brown: {
                background: "rgba(180,120,60,0.15)",
                text: "rgba(200,160,100,0.9)",
              },
              red: {
                background: "rgba(220,50,50,0.12)",
                text: "rgba(240,100,100,0.9)",
              },
              orange: {
                background: "rgba(220,120,30,0.12)",
                text: "rgba(240,160,80,0.9)",
              },
              yellow: {
                background: "rgba(200,180,30,0.12)",
                text: "rgba(230,210,80,0.9)",
              },
              green: {
                background: "rgba(50,180,100,0.12)",
                text: "rgba(100,210,140,0.9)",
              },
              blue: {
                background: "rgba(50,120,220,0.12)",
                text: "rgba(100,160,240,0.9)",
              },
              purple: {
                background: "rgba(150,80,220,0.12)",
                text: "rgba(190,140,240,0.9)",
              },
              pink: {
                background: "rgba(220,80,160,0.12)",
                text: "rgba(240,130,200,0.9)",
              },
            },
          },
          borderRadius: 3,
          fontFamily: "inherit",
        }}
      >
        <SuggestionMenuController
          triggerCharacter="/"
          shouldOpen={(state) =>
            !state.selection.$from.parent.type.isInGroup("tableContent")
          }
          getItems={getSlashItems}
          suggestionMenuComponent={CustomSlashMenu}
        />
        <SideMenuController sideMenu={CompactSideMenu} />
      </BlockNoteView>
    </div>
  );
}
